///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { DialogAction } from './dialog-action-enum';
import { Observable, of } from 'rxjs';
import { inject, Type } from '@angular/core';
import { AbstractDialogComponent } from './abstract-dialog.component';
import { MatDialogData } from './mat-dialog-data';
import { ActivatedRoute, Router } from '@angular/router';
import { NotImplementedException } from './not-implemented-exception';
import { catchError } from 'rxjs/operators';
import { isDefined } from '@siklu/sui-lib/utils';

export abstract class AbstractDialogManager<T, D extends AbstractDialogComponent> {
    protected baseConfig: MatDialogConfig<MatDialogData<T>>;
    protected activatedRoute = inject(ActivatedRoute);
    protected action: DialogAction = this.activatedRoute.snapshot.url[0].path as DialogAction;
    protected dialogComponentClass: Type<D>;

    private readonly router = inject(Router);
    private readonly dialog = inject(MatDialog);

    protected constructor(dialogComponentClass: Type<D>) {
        this.dialogComponentClass = dialogComponentClass;
    }

    private get dialogComponent(): Type<AbstractDialogComponent> {
        return this.dialogComponentClass;
    }

    protected abstract preparingBaseConfig(): void;

    protected findById(id: number | string): Observable<T> {
        throw new NotImplementedException();
    }

    protected openDialog() {
        this.baseConfig = { data: { payload: null } };
        this.preparingBaseConfig();
        this.manageDialogOpening();
    }

    protected manageDialogOpening(): void {
        switch (this.action) {
            case DialogAction.CREATE:
                this.openActionDialog();
                break;
            case DialogAction.EDIT:
            case DialogAction.DELETE:
            case DialogAction.BLOCK:
                this.getEntityAndOpenActionDialog();
                break;
            default:
                this.openActionDialog();
        }
    }

    private openActionDialog(): void {
        const dialogRef: MatDialogRef<AbstractDialogComponent> = this.dialog.open(this.dialogComponent, this.baseConfig);
        this.handleCloseAction(dialogRef);
    }

    private getEntityAndOpenActionDialog(): void {
        const id = this.baseConfig.data.id;

        if (!isDefined(id)) {
            this.comebackToMainComponent();
            return;
        }

        this.findById(id)
            .pipe(
                catchError(() => {
                    return of(undefined);
                })
            )
            .subscribe((entity: T) => {
                if (entity) {
                    this.baseConfig.data.payload = entity;
                    const dialogRef: MatDialogRef<AbstractDialogComponent> = this.dialog.open(this.dialogComponent, this.baseConfig);
                    this.handleCloseAction(dialogRef);
                } else {
                    this.comebackToMainComponent();
                }
            });
    }

    private comebackToMainComponent(): void {
        void this.router.navigate([{ outlets: { popup: null } }], {
            queryParamsHandling: 'preserve',
            relativeTo: this.activatedRoute.parent
        });
    }

    private handleCloseAction(dialogRef: MatDialogRef<AbstractDialogComponent>): void {
        dialogRef.afterClosed().subscribe(() => this.comebackToMainComponent());
    }
}
